import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  alpha,
  Box,
  Collapse,
  List,
  styled,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { Chip, Icon, ImageWithSkeleton, Tag } from '@components/base';
import { TagGrid } from '@components/content';
import { Time } from '@enums/Time';
import useMetadataContext from '@hooks/useMetadataContext';
import { useProviderNames } from '@hooks/useProviderNames';
import useTitleContext from '@hooks/useTitleContext';
import { DetailedTitle } from '@models/Title';
import { AvailabilityMap, getAvailability } from '@utils/availabilityUtils';
import { formatDate } from '@utils/dateUtils';
import { getWideTitleImageUrlWithIndex } from '@utils/imageUtils';
import { getDurStr } from '@utils/titleUtils';

import HighlightedTypography from '../HighlightedTypography';

import EpisodeAiringTimeDisplay from './EpisodeAiringTimeDisplay';

const StyledTableCell = styled(TableCell)`
  padding: ${({ theme }) => theme.spacing()};

  &.image-cell {
    width: 1px;
  }

  &.narrow {
    width: 0px;
  }
`;

const AvailabilityOverlay = styled('div')`
  align-items: center;
  background: ${({ theme }) => alpha(theme.palette.common.black, 0.65)};
  color: ${({ theme }) => theme.palette.common.white};
  content: '';
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

type EpisodeTableRowProps = {
  episode: DetailedTitle;
};

const EpisodeTableRow: FC<EpisodeTableRowProps> = ({ episode }) => {
  const { t } = useTranslation();
  const { highlights, selectTitle } = useMetadataContext();
  const { providerIdentifier } = useTitleContext();
  const { getProviderList } = useProviderNames();

  const [expandAirTimes, setExpandAirTimes] = useState(false);

  const durationStr = getDurStr(t, episode.duration);

  const info = [
    ...(durationStr ? [durationStr] : []),
    ...(episode.productionYear ? [episode.productionYear] : []),
    episode.genres.join(', '),
  ].join(t('middot'));

  const minAvailableFrom = Math.min(
    ...episode.playbackOptions.map(({ availableFrom }) => new Date(availableFrom).getTime())
  );

  const maxAvailableTo = Math.max(
    ...episode.playbackOptions.map(({ availableTo }) => new Date(availableTo).getTime())
  );

  const availability = getAvailability(minAvailableFrom, maxAvailableTo);
  const availabilityDefs = AvailabilityMap[availability];

  const playbackOptionsWithAirTime = episode.playbackOptions.filter(
    playbackOption => !!playbackOption.startTime
  );

  const airTimeCount = 2;

  const hasMoreAirTimes = playbackOptionsWithAirTime.length > airTimeCount;

  return (
    <TableRow
      hover
      key={episode.masterId}
      onClick={() =>
        selectTitle({
          masterId: episode.masterId,
          providerIdentifier,
        })
      }
      selected={!!highlights[episode.masterId]}
    >
      <StyledTableCell className='narrow'>
        <Typography
          title={t('episodeNumber', { episodeNumber: episode.episode?.sequenceNumber })}
          variant='h5'
        >
          {episode.episode?.sequenceNumber}
        </Typography>
      </StyledTableCell>

      <StyledTableCell className='image-cell'>
        <Box position='relative' width={120}>
          <ImageWithSkeleton
            alt={episode.titleName}
            ratio='wide'
            src={getWideTitleImageUrlWithIndex(episode, providerIdentifier, {
              width: 120,
            })}
          />

          {availability === Time.PAST ||
            (availability === Time.FUTURE && (
              <Tooltip
                title={
                  <Trans
                    components={{ strong: <strong /> }}
                    i18nKey={
                      availability === Time.FUTURE
                        ? t('availableFromTime', {
                            time: formatDate(minAvailableFrom),
                          })
                        : t('expired')
                    }
                  />
                }
              >
                <AvailabilityOverlay>
                  {availabilityDefs.icon && <Icon name={availabilityDefs.icon} />}
                </AvailabilityOverlay>
              </Tooltip>
            ))}
        </Box>
      </StyledTableCell>

      <StyledTableCell>
        <HighlightedTypography asset={episode} field='titleName' fontWeight='bold' />

        <HighlightedTypography asset={episode} field='originalTitle' variant='body2' />

        <Typography color='text.secondary' gutterBottom variant='caption'>
          {info}
        </Typography>
      </StyledTableCell>

      <StyledTableCell className='narrow'>
        <List dense>
          {playbackOptionsWithAirTime.slice(0, airTimeCount).map(playbackOption => (
            <EpisodeAiringTimeDisplay key={playbackOption.id} playbackOption={playbackOption} />
          ))}

          {hasMoreAirTimes && (
            <Collapse in={expandAirTimes}>
              {playbackOptionsWithAirTime.slice(airTimeCount).map(playbackOption => (
                <EpisodeAiringTimeDisplay key={playbackOption.id} playbackOption={playbackOption} />
              ))}
            </Collapse>
          )}
        </List>

        {hasMoreAirTimes && (
          <Box display='flex' pt={1}>
            <Tag
              icon={expandAirTimes ? 'expandLess' : 'expandMore'}
              label={expandAirTimes ? t('viewLess') : t('viewMore')}
              onClick={e => {
                e.stopPropagation();
                setExpandAirTimes(prev => !prev);
              }}
              soft
            />
          </Box>
        )}
      </StyledTableCell>

      <StyledTableCell className='narrow'>
        <Chip
          color={availabilityDefs.severity}
          label={t('available', { context: availability })}
          size='small'
          variant='outlined'
        />
      </StyledTableCell>

      <StyledTableCell className='narrow'>{episode.productionYear}</StyledTableCell>

      <StyledTableCell className='narrow'>
        <TagGrid
          tags={getProviderList(providerIdentifier ? [providerIdentifier] : episode.providers)}
        />
      </StyledTableCell>
    </TableRow>
  );
};

export default EpisodeTableRow;
